import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import {
  INITIAL_TOUCHED,
  isConfigFieldDirty,
  isPartFieldDirty,
  tagSignature,
} from './MainPartEditFragment.dirty';
import {
  MainPartEditFragmentHandle,
  MainPartEditFragmentProps,
} from './MainPartEditFragment.props';
import { usePartEditPermissions } from './usePartEditPermissions';
import { Tag } from '../../../../@types';
import {
  PartLocationObject,
  PartObject,
  SfhObject,
  UpdatePartLocationObject,
} from '../../../../@types/part.type';
import { PartType } from '../../../../@types/partType.type';
import {
  AutocompleteTagType,
  getNewTagsMapped,
  getSelectedExistingTagsMapped,
  mapSavedTags,
} from '../../../../components/organisms/Tags';
import { ExceptionPartEdit } from '../../../../components/templates/ExceptionPartEdit';
import { MainPartEdit, MainPartEditTouched } from '../../../../components/templates/MainPartEdit';
import { EntityType } from '../../../../configs/enums';
import {
  SAMPLE_PART,
  SAMPLE_PART_LOCATION,
  SAMPLE_SFH,
  SAMPLE_UPDATED_PART_LOCATION,
} from '../../../../constants/part';
import { SAMPLE_PART_TYPE } from '../../../../constants/partType';
import {
  useGetPartLocationDataByPartId,
  useUpdateExceptionalPart,
  useUpdatePart,
} from '../../../../queries/part-query';
import { useGetOnePartType } from '../../../../queries/part-type-query';
import { useGetAllTags, useAddTags } from '../../../../queries/tags-query';
import { isDropDownEmpty } from '../../../../utils/common';

export const MainPartEditFragment = React.forwardRef<
  MainPartEditFragmentHandle,
  MainPartEditFragmentProps
>(({ data, conditions, duplicatesSlot, onSaved, onDirtyChange }, ref) => {
  const [part, setPart] = useState<PartObject>(SAMPLE_PART);
  const [partType, setPartType] = useState<PartType>(SAMPLE_PART_TYPE);
  const [partLocation, setPartLocation] = useState<PartLocationObject>(SAMPLE_PART_LOCATION);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [newTags, setNewTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<(AutocompleteTagType | string)[]>([]);
  const [partTypeId, setPartTypeId] = useState(0);
  const [partId, setPartId] = useState(0);
  const [updatedLocation, setUpdatedLocation] = useState<UpdatePartLocationObject>(
    SAMPLE_UPDATED_PART_LOCATION
  );
  const partEditPermissions = usePartEditPermissions();
  const [sfhDetails, setSfhDetails] = useState<SfhObject>(SAMPLE_SFH);
  const [isExceptionalPartVisible, setExceptionalPartVisibility] = useState<boolean>(false);
  const [validate, setValidate] = useState(false);
  const [touched, setTouched] = useState<MainPartEditTouched>(INITIAL_TOUCHED);
  const initialTagSigRef = useRef<string>('');

  const updateExceptionalPartQuery = useUpdateExceptionalPart();
  const updateTagsQuery = useAddTags();
  const userTagsQuery = useGetAllTags(EntityType.TYPE_SERIALIESEDPARTS);
  const updatePart = useUpdatePart(data.id);

  const resetForm = (source: PartObject) => {
    const initialTags = mapSavedTags(source.tags);
    setPart(source);
    setPartId(source.id);
    setPartTypeId(source.partTypeId);
    setSelectedTags(initialTags);
    setNewTags([]);
    setUpdatedLocation(SAMPLE_UPDATED_PART_LOCATION);
    setValidate(false);
    setTouched(INITIAL_TOUCHED);
    initialTagSigRef.current = tagSignature(initialTags, []);
  };

  useEffect(() => {
    userTagsQuery.data && setAllTags(userTagsQuery.data);
  }, [userTagsQuery.data]);

  useEffect(() => {
    if (data) {
      resetForm(data);
      if (data.additionalData?.exception) {
        setExceptionalPartVisibility(true);
      }
    }
  }, [data]);

  const getOnePartType = useGetOnePartType(partTypeId);
  const getPartLocationDataByPartId = useGetPartLocationDataByPartId(part.id, updatedLocation);

  useEffect(() => {
    if (!partType) return;
    const toItems = (xs?: { id?: number; version?: string }[]) =>
      (xs ?? []).map((x) => ({ value: x?.id?.toString() || '', label: x?.version || '' }));
    setSfhDetails({
      hardwares: toItems(partType.hardwares),
      softwares: toItems(partType.softwares),
      firmwares: toItems(partType.firmwares),
    });
  }, [partType]);

  useEffect(() => {
    partTypeId && getOnePartType.refetch();
  }, [partTypeId]);

  useEffect(() => {
    (partId || updatedLocation.locationId) && getPartLocationDataByPartId.refetch();
  }, [partId, updatedLocation.locationId]);

  useEffect(() => {
    getOnePartType.data && setPartType(getOnePartType.data);
  }, [getOnePartType.data]);

  useEffect(() => {
    getPartLocationDataByPartId.data && setPartLocation(getPartLocationDataByPartId.data);
  }, [getPartLocationDataByPartId.data]);

  const isConfigDirty = useMemo(
    () => isConfigFieldDirty(part, data, partTypeId),
    [part, data, partTypeId]
  );

  const isDirty = useMemo(() => {
    if (isPartFieldDirty(part, data)) return true;
    if (partTypeId !== data.partTypeId) return true;
    if (updatedLocation.locationId) return true;
    if (newTags.length > 0) return true;
    return tagSignature(selectedTags, newTags) !== initialTagSigRef.current;
  }, [part, data, partTypeId, updatedLocation.locationId, selectedTags, newTags]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleSave = async () => {
    setValidate(true);
    if (isDropDownEmpty(part.conditionCode)) return;
    if (!part.serial1?.trim()) return;
    if (isConfigDirty && !part.configChnageNotes?.trim()) return;

    const existingTags = getSelectedExistingTagsMapped(selectedTags);
    const freshTags = getNewTagsMapped(newTags);

    await updatePart.mutateAsync({
      ...part,
      partTypeId,
      locationId: updatedLocation.locationId,
      locationTypeCode: updatedLocation.locationTypeCode,
      locationHierarchy: updatedLocation.locationHierarchy,
    });

    await updateTagsQuery.mutateAsync({
      entityTypeId: EntityType.TYPE_SERIALIESEDPARTS,
      entityId: data.id,
      freshTags,
      existingTags,
    });

    setPart((prev) => ({ ...prev, partTypeId, configChnageNotes: '' }));
    setTouched(INITIAL_TOUCHED);
    setValidate(false);
    onSaved?.();
  };

  const handleExceptionalPartUpdate = async (
    exceptionalData: Parameters<typeof updateExceptionalPartQuery.mutateAsync>[0]
  ) => {
    const response = await updateExceptionalPartQuery.mutateAsync(exceptionalData);
    if (response) setExceptionalPartVisibility(false);
  };

  useImperativeHandle(
    ref,
    () => ({
      save: handleSave,
      reset: () => resetForm(data),
      isSaving: () => updatePart.isPending || updateTagsQuery.isPending,
      isDirty: () => isDirty,
    }),
    // resetForm closes over setters which are stable; handleSave depends on form
    // state, so we re-derive the handle whenever any of those change.
    [
      data,
      part,
      partTypeId,
      selectedTags,
      newTags,
      updatedLocation,
      updatePart.isPending,
      updateTagsQuery.isPending,
      isDirty,
    ]
  );

  const exceptionSlot =
    partEditPermissions.isExceptionalAcceptance && isExceptionalPartVisible ? (
      <ExceptionPartEdit data={data} onRespond={handleExceptionalPartUpdate} />
    ) : undefined;

  return (
    <MainPartEdit
      permissions={partEditPermissions}
      value={part}
      sfhDetails={sfhDetails}
      conditions={conditions}
      selectedTags={selectedTags}
      allTags={allTags}
      newTags={newTags}
      setNewTags={setNewTags}
      partType={partType}
      partLocation={partLocation}
      setSelectedTags={setSelectedTags}
      isConfigDirty={isConfigDirty}
      touched={touched}
      setTouched={setTouched}
      exceptionSlot={exceptionSlot}
      duplicatesSlot={duplicatesSlot}
      handlePartTypeChange={(val) => setPartTypeId(val.partTypeId)}
      handlePartLocationChange={(val) => {
        setPartId(val.id);
        setUpdatedLocation({
          id: val.id,
          locationId: val.locationId,
          locationHierarchy: val.locationHierarchy,
          locationTypeCode: val.locationTypeCode,
        });
      }}
      onChange={(val) => setPart({ ...val })}
      validate={validate}
    />
  );
});

MainPartEditFragment.displayName = 'MainPartEditFragment';
